import { Injectable, inject } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { DateService } from '../../../services/date/date.service';
import { RapCodeType, AccountType } from '../../../domain/account.domain';
import { CashTransferAccount, Day, FrequencyTypes } from '../cash/cash-transfer.domain';
import { DataAnalyticsTaggingService } from '../../../features/data-analytics-tagging/data-analytics-tagging.service';
import { TransferService } from '../transfer.service';
import { DataAnalyticsTaggingConstants } from '../../../features/data-analytics-tagging/data-analytics-tagging.constants';
import { OfiTransfer, OfiTransferRequest } from './ofi-transfers.domain';

@Injectable()
export class OfiTransferService {
  private readonly dataAnalyticsTaggingService = inject(DataAnalyticsTaggingService);
  private readonly transferService = inject(TransferService);

  setDataToForm(ofiTransferRequest: OfiTransferRequest, ofiTransferData: OfiTransfer): OfiTransferRequest {
    ofiTransferRequest.contributionType = ofiTransferData.contributionType;
    ofiTransferRequest.externalAccountNumber = ofiTransferData.externalAccountNumber;
    ofiTransferRequest.externalInstitutionNumber = ofiTransferData.externalInstitutionNumber;
    ofiTransferRequest.externalTransitNumber = ofiTransferData.externalTransitNumber;
    ofiTransferRequest.frequencyType = ofiTransferData.frequencyType;
    ofiTransferRequest.isRecurring = ofiTransferData.isRecurring;
    ofiTransferRequest.voidCheque = ofiTransferData.voidCheque;
    ofiTransferRequest.startDate = ofiTransferData.startDate;
    ofiTransferRequest.transferAmount = ofiTransferData.transferAmount;
    return ofiTransferRequest;
  }

  getWeeklyDates(): Array<Date> {
    let numOfDays = 1;
    const weeklyDates = [];
    while (weeklyDates.length < 5) {
      const tempDate = DateService.getDateFrom(new Date(), numOfDays);
      if (!DateService.isWeekend(tempDate)) {
        weeklyDates.push(tempDate);
      }
      numOfDays++;
    }
    return weeklyDates;
  }

  getWeeklyDate(ofiTransferRequest: OfiTransferRequest): Day {
    return Object.values(Day)[ofiTransferRequest.startDate.getDay() - 1];
  }

  getBiWeeklyDate(): Date {
    return DateService.getDateFrom(new Date(), 14);
  }

  getMonthlyDate(): Date {
    return DateService.getDateFrom(new Date(), 30);
  }

  getSemiMonthlyStartDate(): Date {
    let startDate = DateService.getDateFrom(new Date(), 3);
    const dayOfMonth = startDate.getDate();
    if (dayOfMonth !== 1 && dayOfMonth !== 15) {
      if (dayOfMonth < 15) {
        startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 15);
      } else {
        startDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1);
      }
    }
    return startDate;
  }

  getThirdBusinessDay(): Date {
    const today = new Date();
    if (today.getDay() < 3) {
      return DateService.getDateFrom(today, 3);
    } else if (today.getDay() === 6) {
      return DateService.getDateFrom(today, 4);
    } else {
      return DateService.getDateFrom(today, 5);
    }
  }

  isValidDate(frequencyType: FrequencyTypes, startDate: Date, minDate: Date, maxDate: Date): boolean {
    return ((frequencyType === FrequencyTypes.BI_WEEKLY && !DateService.isWeekend(startDate))
      || frequencyType === FrequencyTypes.MONTHLY)
      && DateService.isDate(startDate)
      && startDate.setHours(0, 0, 0, 0) >= minDate.setHours(0, 0, 0, 0)
      && startDate.setHours(0, 0, 0, 0) <= maxDate.setHours(0, 0, 0, 0);
  }

  selectDay(day: string, weeklyDates: Array<Date>): Date {
    const days: Array<string> = Object.keys(Day);
    return weeklyDates.filter((date: Date): boolean =>
      date.getDay() === days.indexOf(day) + 1
    )[0];
  }

  getAmountHint(ofiTransferRequest: OfiTransferRequest | OfiTransfer): string {
    switch (ofiTransferRequest.frequencyType) {
      case FrequencyTypes.WEEKLY:
        return 'WEEKLY_AMOUNT_NOTE';
      case FrequencyTypes.BI_WEEKLY:
        return 'BI_WEEKLY_AMOUNT_NOTE';
      case FrequencyTypes.SEMI_MONTHLY:
        return 'SEMI_MONTHLY_AMOUNT_NOTE';
      case FrequencyTypes.MONTHLY:
        return 'MONTHLY_AMOUNT_NOTE';
      default:
        return 'ONE_TIME_AMOUNT_NOTE';
    }
  }

  isAmountValueExceeded(ofiTransferRequest: OfiTransferRequest | OfiTransfer): ValidationErrors | null {
    let validationError: ValidationErrors;
    if (ofiTransferRequest?.transferAmount < this.getMinAmount(ofiTransferRequest)) {
      validationError = { min: true };
    }
    // Max Amount Validator
    if (ofiTransferRequest?.transferAmount > this.getMaxAmount(ofiTransferRequest)) {
      validationError = { max: true };
    }
    return validationError;
  }

  isInstitutionNumberInvalid(ofiTransferRequest: OfiTransferRequest): boolean {
    if (ofiTransferRequest.externalInstitutionNumber &&
      ofiTransferRequest.externalInstitutionNumber.toString() === '004') {
      return true;
    } else {
      return false;
    }
  }

  isContributionTypeDisplayed(selectedToAccount: CashTransferAccount): boolean {
    const toAccountRapCode = (selectedToAccount !== undefined
      && selectedToAccount.rapCode !== undefined) ?
      selectedToAccount.rapCode.name : '';
    return toAccountRapCode === RapCodeType.BASIC_RSP ||
      toAccountRapCode === RapCodeType.SDRSP;
  }

  isToAccountRrsp(ofiTransfer: OfiTransfer): boolean {
    return ofiTransfer.selectedToAccount?.rapCode?.acctType === AccountType.SDRSP ||
      ofiTransfer.selectedToAccount?.rapCode?.acctType === AccountType.BASIC_RSP;
  }

  isToAccountTfsa(ofiTransfer: OfiTransfer): boolean {
    return ofiTransfer.selectedToAccount?.rapCode?.acctType === AccountType.TFSA;
  }

  getMaxAmount(ofiTransferRequest: OfiTransferRequest | OfiTransfer): number {
    switch (ofiTransferRequest.frequencyType) {
      case FrequencyTypes.WEEKLY:
        return 1250;
      case FrequencyTypes.BI_WEEKLY:
      case FrequencyTypes.SEMI_MONTHLY:
        return 2500;
      case FrequencyTypes.MONTHLY:
        return 5000;
      default:
        return 25000;
    }
  }

  setWBAnalyticsDataForAmountError(ofiTransferRequest: OfiTransferRequest | OfiTransfer): void {
    const maxAmountAllowedBasedUponFreq = this.getMaxAmount(ofiTransferRequest);
    const amountSubmitted = ofiTransferRequest.transferAmount;
    if (amountSubmitted !== undefined && ofiTransferRequest.frequencyType !== undefined &&
      amountSubmitted > maxAmountAllowedBasedUponFreq && ofiTransferRequest.isRecurring) {
      const message = `Maximum ${this.transferService.getFrequencyValueForTagging(ofiTransferRequest.frequencyType)} amount is $${maxAmountAllowedBasedUponFreq}`
      const eventData = DataAnalyticsTaggingConstants.getErrorPageEventData(message, 'ofi', 'wb_modal-transfers-ofi');
      this.dataAnalyticsTaggingService.emitTaggingEvent(eventData);
    }
  }

  private getMinAmount(ofiTransferRequest: OfiTransferRequest | OfiTransfer): number {
    switch (ofiTransferRequest.frequencyType) {
      case FrequencyTypes.WEEKLY:
      case FrequencyTypes.BI_WEEKLY:
      case FrequencyTypes.SEMI_MONTHLY:
      case FrequencyTypes.MONTHLY:
        return 0.01;
      default:
        return 10;
    }
  }
}

